import {
  Box,
  CircularProgress,
  InputAdornment,
  Typography,
  useTheme,
} from '@mui/material';
import { IconSearch } from '@tabler/icons-react';
import { navigateBack, navigateTo } from 'actions/main';
import { recordKnowledgeClick } from 'actions/workflows-v2/workflowsV2';
import { setSelectedArticle } from 'actions/workflows/workflows';
import { KnowledgeResult, KnowledgeSearchResponse } from 'api/types';
import { TextInput } from 'components/TextInput/TextInput';
import { ARTICLE_PAGE } from 'constants/pageConsts';
import { useAppDispatch, useAppSelector } from 'hooks';
import React from 'react';
import {
  selectKnowledgeSearchQuery,
  selectWidgetSearchLoading,
  selectWidgetSearchResult,
  setKnowledgeSearchQuery,
} from 'reducers/workflows';
import { htmlDecode } from 'utils/htmlDecode';
import emptyKnowledgeSvg from './knowledge-search-empty.svg';
import { useWidgetArticleSearch } from 'components/App/hooks/useWidgetArticleSearch';
import { BackArrowButton } from 'components/ArticlePage/ArticlePage';

export const KnowledgeSearchPage = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const knowledgeSearchQuery = useAppSelector(selectKnowledgeSearchQuery);
  const widgetSearchResult = useAppSelector(selectWidgetSearchResult);
  const isWidgetSearchLoading = useAppSelector(selectWidgetSearchLoading);

  // used to trigger the search when the knowledgeSearchQuery changes
  useWidgetArticleSearch({
    origin: 'search_page',
    query: knowledgeSearchQuery,
  });

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setKnowledgeSearchQuery(e.target.value));
  };

  const onSendMessage = () => {
    // do nothing
  };

  const onResultClick = ({
    document,
    fullResult,
  }: {
    document: KnowledgeResult;
    fullResult: KnowledgeSearchResponse;
  }) => {
    dispatch(
      setSelectedArticle({
        ...document,
        link: document.link || '',
      }),
    );
    dispatch(navigateTo(ARTICLE_PAGE));
    dispatch(recordKnowledgeClick({ document, fullResult }));
  };

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        gap: '16px',
        height: '100%',
        overflow: 'hidden',
        padding: '16px',
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 2,
        }}
      >
        <BackArrowButton
          aria-label='Return to chat'
          onClick={() => {
            dispatch(setSelectedArticle(undefined));
            dispatch(navigateBack());
          }}
          type='button'
        />
        <TextInput
          aria-label='knowledge'
          disableSendMessage
          EndAdornment={
            /**
             * Need to add an empty component to because TextInput
             * has a default send message end adornment.
             */
            <InputAdornment position='end'>
              <React.Fragment></React.Fragment>
            </InputAdornment>
          }
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                {isWidgetSearchLoading ? (
                  <CircularProgress
                    size={20}
                    sx={{ color: theme.palette.colors.grey[500] }}
                  />
                ) : (
                  <IconSearch
                    color={theme.palette.colors.grey[500]}
                    size={24}
                  />
                )}
              </InputAdornment>
            ),
          }}
          onChange={onChange}
          onSendMessage={onSendMessage}
          placeholder='Type a keyword'
          showActions={false}
          value={knowledgeSearchQuery}
        />
      </Box>
      {widgetSearchResult ? (
        <Box
          className='forethought-scrollbar'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            overflowX: 'hidden',
            overflowY: 'auto',
          }}
        >
          {widgetSearchResult.results.map((result, index) => (
            <Box
              key={index}
              onClick={() => {
                onResultClick({
                  document: result,
                  fullResult: widgetSearchResult,
                });
              }}
              sx={{
                border: `1px solid ${theme.palette.colors.grey[200]}`,
                borderRadius: '8px',
                cursor: 'pointer',
                gap: '4px',
                padding: '10px',
              }}
            >
              <Typography component='div' variant='font14'>
                {result.title}
              </Typography>
              <Box
                sx={{
                  color: theme.palette.colors.grey[500],
                  fontSize: '12px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <Typography
                  color={theme.palette.colors.grey[500]}
                  variant='font12'
                >
                  {htmlDecode(result.preview)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            gap: '16px',
            justifyContent: 'center',
          }}
        >
          <img alt='No results found' src={emptyKnowledgeSvg} />
          <Typography color={theme.palette.colors.grey[700]} variant='font16'>
            Type a keyword to find articles
          </Typography>
        </Box>
      )}
    </Box>
  );
};
